import Table from "@/common/components/Table";
import { useState } from "react";
import {
  AuditTrail,
  ExpiredDrugsStats,
  FinancialReport,
  StockLevel,
} from "@/common/constants";
import useQuery from "@/hooks/useQuery";
import { KeyValueObj } from "@/common/types";
import { formatCurrency } from "@/common/components/utilities/formatCurrency";

import "jspdf-autotable";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import "jspdf";
import {
  auditTrailOptions,
  ExpiredDrugsfilterOptions,
  FinancialfilterOptions,
  StockReportfilterOptions,
} from "./datums";
import { toParamsFormat } from "@/common/utils/general";

function extractDate(input: string): string {
  if (input === undefined || input === null) {
    return "0";
  }

  const date = new Date(input);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
// ******* Stock Level Report Table ******
export function StockLevelRecord() {
  const [search, setSearch] = useState("");

  const {
    data: stockLevel,
    loading: loading,
    refetch,
  } = useQuery(StockLevel + `?search=${search}`);
  function handleFilter(values: KeyValueObj) {
    let params = toParamsFormat(values);
    refetch(StockLevel + params);
  }

  function handleSearch(value: string) {
    setSearch(value);
  }

  const TableHeader = [
    "ATC Code-Name",
    "Packaging Type",
    "Qty Available",
    "Threshold",
    "% Below Threshold",
    "Last Restocked",
  ];

  const TableKey = [
    "atc_code",
    "packaging_type",
    "qty_available",
    "threshold",
    "below_threshold",
    "last_restocked",
  ];

  function getTableData(data: KeyValueObj[]) {
    const stockLevelReport = data?.map((datum: KeyValueObj) => ({
      id: datum?.id,
      atc_code: {
        type: "string",
        value: datum?.atc_code,
      },
      packaging_type: {
        type: "string",
        value: datum?.package_type,
      },

      qty_available: {
        type: "string",
        value: datum?.quantity_available,
      },
      threshold: {
        type: "string",
        value: datum?.threshold,
      },

      date_to_expiry: {
        type: "string",
        value: datum?.days_to_expiry,
      },
      below_threshold: {
        type: "string",
        value: datum?.below_threshold,
      },
      last_restocked: {
        type: "string",
        value: datum?.last_restocked_date,
      },
    }));
    return stockLevelReport;
  }

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Stock Report table", 14, 15);

    // Build headers
    const headers = TableHeader.map((header) =>
      typeof header === "string" ? header : ""
    );

    const body = stockLevel.map(
      (item: {
        atc_code: string;
        package_type: string;
        quantity_available: string;
        threshold: string;
        below_threshold: string;
        last_restocked_date: string;
      }) => [
        String(item.atc_code),
        String(item.package_type),
        String(item.quantity_available),
        String(item.threshold),
        String(item.below_threshold),
        String(extractDate(item.last_restocked_date)),
      ]
    );

    autoTable(doc, {
      head: [headers],
      body: body,
      startY: 25,
      styles: { fontSize: 10 },
      theme: "grid",
    });
    doc.save("Stock Report Level.pdf");
  };
  return (
    <>
      <div>
        <div className="flex justify-end m-5 gap-5"></div>
        <Table
          tableHeaders={TableHeader}
          tableData={getTableData(stockLevel)}
          tableKeys={TableKey}
          tableTitle="Drug Stock Level"
          shouldHover
          loading={loading}
          headerAction={{
            label: "Export Report",
            action: exportPDF,
          }}
          filterInputs={StockReportfilterOptions}
          handleFilter={handleFilter}
          handleSearch={handleSearch}
        />
      </div>
    </>
  );
}

// ******* Drug Expiry Table ******
export function DrugExpiryRecord() {
  const [search, setSearch] = useState("");
  const {
    data: expired,
    loading: loading,
    refetch,
  } = useQuery(ExpiredDrugsStats + `?search=${search}`);

  function handleFilter(values: KeyValueObj) {
    let params = toParamsFormat(values);
    refetch(StockLevel + params);
  }

  function handleSearch(value: string) {
    setSearch(value);
  }

  const TableHeader = [
    "ATC Code-Name",
    "Batch Number",
    "Manufacture Date",
    "Expiry Date",
    "Days to Expiry",
  ];

  const TableKey = [
    "atc_code",
    "batch_number",
    "manufacturer",
    "expired_date",
    "date_to_expiry",
  ];

  function getTableData(data: KeyValueObj[]) {
    const expiredDrugs = data?.map((datum: KeyValueObj) => ({
      id: datum?.id,
      atc_code: {
        type: "string",
        value: datum?.atc_code,
      },
      batch_number: {
        type: "string",
        value: datum?.batch_number,
      },

      manufacturer: {
        type: "string",
        value: datum?.manufacture_date,
      },
      expired_date: {
        type: "string",
        value: datum?.expiry_date,
      },

      date_to_expiry: {
        type: "string",
        value: datum?.days_to_expiry,
      },
    }));
    return expiredDrugs;
  }

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Expiry drug table", 14, 15);

    // Build headers
    const headers = TableHeader.map((header) =>
      typeof header === "string" ? header : ""
    );

    const body = expired?.map(
      (item: {
        atc_code: string;
        batch_number: string;
        manufacturing_date: string;
        expiry_date: string;
        date_to_expiry: string;
      }) => [
        String(item.atc_code),
        String(item.batch_number),
        String(item.manufacturing_date),
        String(item.expiry_date),
        String(item.date_to_expiry),
      ]
    );

    autoTable(doc, {
      head: [headers],
      body: body,
      startY: 25,
      styles: { fontSize: 10 },
      theme: "grid",
    });
    doc.save("Expiry Drug report.pdf");
  };
  return (
    <>
      <div>
        <div className="flex justify-end m-5 gap-5"></div>
        <Table
          tableHeaders={TableHeader}
          tableData={getTableData(expired) || []}
          tableKeys={TableKey}
          tableTitle="Expired Drugs Report"
          loading={loading}
          shouldHover
          headerAction={{
            label: "Export Report",
            action: exportPDF,
          }}
          filterInputs={ExpiredDrugsfilterOptions}
          handleSearch={handleSearch}
          handleFilter={handleFilter}
        />
      </div>
    </>
  );
}

// ******* Financial Table ******

export function FinancialRecord() {
  const [search, setSearch] = useState("");
  const {
    data: data,
    loading: loading,
    refetch,
  } = useQuery(FinancialReport + `?search=${search}`);

  function handleFilter(values: KeyValueObj) {
    let params = toParamsFormat(values);
    refetch(FinancialReport + params);
  }

  function handleSearch(value: string) {
    setSearch(value);
  }

  const TableHeader = [
    "Month",
    "Total Sales",
    "Sales Revenue",
    "Restocking Cost",
    "Net Balance",
  ];

  const TableKey = [
    "month",
    "total_sales",
    "sales_revenue",
    "restocking_cost",
    "net_balance",
  ];

  function getTableData(data: KeyValueObj[]) {
    const financialReport = data?.map((datum: KeyValueObj) => ({
      id: datum?.id,
      month: {
        type: "string",
        value: datum?.month,
      },
      total_sales: {
        type: "string",
        value: formatCurrency(datum?.total_sales),
      },

      sales_revenue: {
        type: "string",
        value: formatCurrency(datum?.sales_revenue),
      },
      restocking_cost: {
        type: "string",
        value: formatCurrency(datum?.restocking_cost),
      },

      net_balance: {
        type: "string",
        value: formatCurrency(datum?.net_balance),
      },
    }));
    return financialReport;
  }
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Financial Report table", 14, 15);

    // Build headers
    const headers = TableHeader.map((header) =>
      typeof header === "string" ? header : ""
    );

    const body = data?.data?.map(
      (item: {
        month: string;
        total_sales: string;
        sales_revenue: string;
        restocking_cost: string;
        net_balance: string;
      }) => [
        item.month,
        item.total_sales,
        item.sales_revenue,
        item.restocking_cost,
        item.net_balance,
      ]
    );

    autoTable(doc, {
      head: [headers],
      body: body,
      startY: 25,
      styles: { fontSize: 10 },
      theme: "grid",
    });
    doc.save("Financial Report.pdf");
  };

  return (
    <>
      <div>
        <div className="flex justify-end m-5 gap-5"></div>
        <Table
          tableHeaders={TableHeader}
          tableKeys={TableKey}
          tableData={getTableData(data?.data)}
          tableTitle="Financial Report"
          loading={loading}
          shouldHover
          headerAction={{
            label: "Export Report",
            action: exportPDF,
          }}
          filterInputs={FinancialfilterOptions}
          handleFilter={handleFilter}
          handleSearch={handleSearch}
        />
      </div>
    </>
  );
}

// ********** Audit Trail ********

export function AuditTrailRecord() {
  const [search, setSearch] = useState("");
  const {
    data: auditTrail,
    loading: loading,
    refetch,
  } = useQuery(AuditTrail + `?search=${search}`);
  const TableHeader = ["Date", "Time", "User", "Action", "Details"];
  const TableKey = ["date", "time", "user", "action", "details"];

  function handleFilter(values: KeyValueObj) {
    let params = toParamsFormat(values);
    refetch(AuditTrail + params);
  }

  function handleSearch(value: string) {
    setSearch(value);
  }

  function getTableData(data: KeyValueObj[]) {
    const audiTrail = data?.map((datum: KeyValueObj) => ({
      id: datum?.id,
      date: {
        type: "string",
        value: extractDate(datum?.created_at),
      },
      time: {
        type: "string",
        value: datum?.timestamp,
      },
      user: {
        type: "string",
        value: datum?.user,
      },
      action: {
        type: "string",
        value: datum?.action,
      },
      details: {
        type: "string",
        value: datum?.details?.justification,
      },
    }));
    return audiTrail;
  }

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("AuditTrail Report table", 14, 15);

    // Build headers
    const headers = TableHeader.map((header) =>
      typeof header === "string" ? header : ""
    );

    const body = auditTrail?.map(
      (item: {
        created_at: string;
        timestamp: string;
        user: string;
        action: string;
        justification: string;
      }) => [
        String(item?.created_at),
        String(item?.timestamp),
        String(item?.user),
        String(item?.action),
        String(item?.justification),
      ]
    );

    autoTable(doc, {
      head: [headers],
      body: body,
      startY: 25,
      styles: { fontSize: 10 },
      theme: "grid",
    });
    doc.save("Audit trail report.pdf");
  };

  return (
    <>
      <div>
        <div className="flex justify-end m-5 gap-5"></div>
        <Table
          tableHeaders={TableHeader}
          tableKeys={TableKey}
          tableData={getTableData(auditTrail)}
          tableTitle="Audit Trail"
          loading={loading}
          headerAction={{
            label: "Export Report",
            action: exportPDF,
          }}
          handleFilter={handleFilter}
          filterInputs={auditTrailOptions}
          handleSearch={handleSearch}
        />
      </div>
    </>
  );
}
